/** S6.2/S8.3: fila derivada de `dirtyAt` e dos recibos de marca na própria LocalList. */
import { getList, getLists, type LocalList } from '../data/localDb';
import {
  hasPendingListBackupMarkers,
  uploadListBackup,
  type ListBackupResult,
} from './listBackup';

export const BACKUP_QUEUE_RESCAN_MS = 5_000;
export const BACKUP_QUEUE_LOCK_PREFIX = 'eminer:list-backup:';

interface QueueStorage {
  getList: typeof getList;
  getLists: typeof getLists;
}

export interface BackupQueueLock {
  run<T>(userId: string, task: () => Promise<T>): Promise<T>;
}

export interface ListBackupQueueDependencies {
  storage: QueueStorage;
  uploadList: (listId: string) => Promise<ListBackupResult>;
  lock: BackupQueueLock;
  now: () => number;
  setTimer: (callback: () => void, delay: number) => ReturnType<typeof setTimeout>;
  clearTimer: (timer: ReturnType<typeof setTimeout>) => void;
}

interface BrowserLockManager {
  request<T>(
    name: string,
    options: { mode: 'exclusive' },
    callback: () => Promise<T>,
  ): Promise<T>;
}

const BROWSER_LOCK: BackupQueueLock = {
  async run<T>(userId: string, task: () => Promise<T>): Promise<T> {
    const name = `${BACKUP_QUEUE_LOCK_PREFIX}${encodeURIComponent(userId)}`;
    const locks = typeof navigator === 'undefined'
      ? undefined
      : (navigator as Navigator & { locks?: BrowserLockManager }).locks;
    if (!locks) return runWithMemoryLock(name, task);

    // O painel só monta em Chromium desktop. O lock é por conta e cobre a varredura inteira:
    // a segunda aba espera, relê o IndexedDB e não reenvia uma lista que a primeira já limpou.
    return locks.request(
      name,
      { mode: 'exclusive' },
      task,
    );
  },
};

const memoryLockTails = new Map<string, Promise<void>>();

/** Fallback na mesma aba; entre abas, Chromium usa Web Locks. */
async function runWithMemoryLock<T>(name: string, task: () => Promise<T>): Promise<T> {
  const previous = (memoryLockTails.get(name) ?? Promise.resolve()).catch(() => undefined);
  let release!: () => void;
  const current = new Promise<void>((resolve) => {
    release = resolve;
  });
  const tail = previous.then(() => current);
  memoryLockTails.set(name, tail);
  await previous;
  try {
    return await task();
  } finally {
    release();
    if (memoryLockTails.get(name) === tail) memoryLockTails.delete(name);
  }
}

/** Compartilhado com S10.1 para exclusão e upload nunca observarem a lista em paralelo. */
export function withListBackupLock<T>(userId: string, task: () => Promise<T>): Promise<T> {
  return BROWSER_LOCK.run(userId, task);
}

const DEFAULT_DEPENDENCIES: ListBackupQueueDependencies = {
  storage: { getList, getLists },
  uploadList: uploadListBackup,
  lock: BROWSER_LOCK,
  now: () => Date.now(),
  setTimer: (callback, delay) => setTimeout(callback, delay),
  clearTimer: (timer) => clearTimeout(timer),
};

function timestamp(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Elegibilidade do varrimento periódico. `error` fica de fora de propósito: ao esgotar as
 * tentativas o registro perde o `backupNextAttemptAt`, então voltaria a cada rescan e bateria
 * no servidor de 5 em 5 s.
 *
 * A retomada de `error` acontece só nos gatilhos grossos — reabrir o painel e voltar da rede —,
 * por `triggerUser(..., includeError)`. Antes o resgate dependia de um botão na coluna "Backup";
 * a coluna saiu (o backup é silencioso) e, sem essa retomada automática, uma lista que esgotasse
 * as tentativas ficaria sem cópia para sempre, sem ninguém saber.
 */
function isAutomaticallyEligible(list: LocalList): boolean {
  return hasBackupWork(list) && list.backupState !== 'error';
}

function hasBackupWork(list: LocalList): boolean {
  return Boolean(list.dirtyAt) || hasPendingListBackupMarkers(list);
}

/**
 * O motor não guarda itens próprios: toda execução relê `lists` e deriva o trabalho do conteúdo
 * sujo ou de uma marca sem recibo. Timers são só despertadores; fechá-los não perde a fila.
 */
export class ListBackupQueue {
  private activeUserId: string | null = null;
  private generation = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private tail: Promise<void> = Promise.resolve();

  constructor(private readonly dependencies: ListBackupQueueDependencies = DEFAULT_DEPENDENCIES) {}

  activate(userId: string): Promise<void> {
    if (!userId) return Promise.resolve();
    if (this.activeUserId !== userId) {
      this.clearScheduledTimer();
      this.activeUserId = userId;
      this.generation += 1;
    }
    // Reabrir o painel é um dos dois momentos em que `error` volta a ser tentado.
    return this.triggerUser(userId, false, true);
  }

  deactivate(userId: string): void {
    if (this.activeUserId !== userId) return;
    this.activeUserId = null;
    this.generation += 1;
    this.clearScheduledTimer();
  }

  /**
   * `online` é apenas um sinal: antecipa a tentativa, mas a falha segue o backoff normal.
   * É o outro momento em que `error` volta a ser tentado — rede de volta é a causa mais
   * comum de uma lista ter esgotado as tentativas.
   */
  online(userId: string): Promise<void> {
    if (this.activeUserId !== userId) return Promise.resolve();
    return this.triggerUser(userId, true, true);
  }

  /** Fim/parada/pausa tentam a lista agora, sem fazer o chamador esperar pela rede. */
  requestList(listId: string): Promise<void> {
    return this.enqueue(async () => {
      const candidate = await this.dependencies.storage.getList(listId);
      if (!candidate || !isAutomaticallyEligible(candidate)) return;
      if (this.activeUserId && candidate.userId !== this.activeUserId) return;

      await this.dependencies.lock.run(candidate.userId, async () => {
        const current = await this.dependencies.storage.getList(listId);
        if (!current || !isAutomaticallyEligible(current)) return;
        if (this.activeUserId && current.userId !== this.activeUserId) return;
        await this.tryUpload(current.id);
      });

      if (this.activeUserId === candidate.userId) {
        await this.scheduleNext(candidate.userId, this.generation);
      }
    });
  }

  reset(): void {
    this.activeUserId = null;
    this.generation += 1;
    this.clearScheduledTimer();
    this.tail = Promise.resolve();
  }

  /**
   * `includeError` só é `true` nas duas chamadas de entrada (`activate`, `online`) — reabrir o
   * painel e voltar da rede, os dois gatilhos que substituem o botão manual removido. O rescan
   * periódico (o `setTimer` no fim deste arquivo) sempre chama com `false`: sem isso, uma lista
   * `error` seria retentada a cada `BACKUP_QUEUE_RESCAN_MS` (5 s) para sempre, batendo no
   * servidor sem parar por uma falha que já se provou persistente.
   */
  private triggerUser(userId: string, ignoreBackoff: boolean, includeError = false): Promise<void> {
    const generation = this.generation;
    return this.enqueue(async () => {
      if (this.activeUserId !== userId || this.generation !== generation) return;
      await this.dependencies.lock.run(userId, async () => {
        if (this.activeUserId !== userId || this.generation !== generation) return;
        const lists = await this.dependencies.storage.getLists(userId);
        const now = this.dependencies.now();
        for (const list of lists) {
          if (!hasBackupWork(list)) continue;
          if (!includeError && list.backupState === 'error') continue;
          const nextAttemptAt = timestamp(list.backupNextAttemptAt);
          if (!ignoreBackoff && nextAttemptAt !== undefined && nextAttemptAt > now) continue;
          await this.tryUpload(list.id);
        }
      });
      await this.scheduleNext(userId, generation);
    });
  }

  private async tryUpload(listId: string): Promise<void> {
    try {
      await this.dependencies.uploadList(listId);
    } catch {
      // `dirtyAt` continua sendo a fila. Uma falha inesperada de infraestrutura não pode
      // bloquear as demais listas nem escapar para a captura/UI.
    }
  }

  private async scheduleNext(userId: string, generation: number): Promise<void> {
    if (this.activeUserId !== userId || this.generation !== generation) return;
    const lists = await this.dependencies.storage.getLists(userId);
    if (this.activeUserId !== userId || this.generation !== generation) return;

    const now = this.dependencies.now();
    let delay: number | undefined;
    for (const list of lists) {
      if (!isAutomaticallyEligible(list)) continue;
      const nextAttemptAt = timestamp(list.backupNextAttemptAt);
      const candidateDelay = nextAttemptAt === undefined
        ? BACKUP_QUEUE_RESCAN_MS
        : Math.max(BACKUP_QUEUE_RESCAN_MS, nextAttemptAt - now);
      delay = delay === undefined ? candidateDelay : Math.min(delay, candidateDelay);
    }

    this.clearScheduledTimer();
    if (delay === undefined) return;
    this.timer = this.dependencies.setTimer(() => {
      this.timer = null;
      if (this.activeUserId === userId && this.generation === generation) {
        void this.triggerUser(userId, false);
      }
    }, delay);
  }

  private enqueue(task: () => Promise<void>): Promise<void> {
    const current = this.tail.catch(() => undefined).then(task);
    this.tail = current.catch(() => undefined);
    return current;
  }

  private clearScheduledTimer(): void {
    if (this.timer === null) return;
    this.dependencies.clearTimer(this.timer);
    this.timer = null;
  }
}

let defaultQueue = new ListBackupQueue();

export function activateListBackupQueue(userId: string): void {
  void defaultQueue.activate(userId);
}

export function deactivateListBackupQueue(userId: string): void {
  defaultQueue.deactivate(userId);
}

export function notifyListBackupOnline(userId: string): void {
  void defaultQueue.online(userId);
}

export function requestListBackup(listId: string): Promise<void> {
  return defaultQueue.requestList(listId);
}

export function __resetListBackupQueue(): void {
  defaultQueue.reset();
  defaultQueue = new ListBackupQueue();
  memoryLockTails.clear();
}
